import type { Answers } from "./_schema";
import type { DayPoint } from "./_focus-schema";

/** Sampled hours. Same for everyone, so two curves are always comparable. */
export const DAY_HOURS = [6, 7, 8, 9, 10, 12, 14, 16, 18, 20, 22] as const;

function str(answers: Answers, id: string): string | undefined {
  const v = answers[id];
  return typeof v === "string" ? v : undefined;
}

const clamp = (v: number) => Math.min(1, Math.max(0, Number(v.toFixed(3))));

/**
 * The coffee line's shape, SIGNED around where he wakes: +1 is the morning
 * peak, -1 is the afternoon floor, 0 is the level he started the day at.
 * Signed rather than 0..1 so hour six sits exactly at his waking level -- he
 * has not taken anything yet, so the two lines must start together.
 *
 * Flat, first-cup lift to a peak at eight, decay back through baseline by
 * midday, the afternoon wall at two, a small second-cup bump at four, then a
 * low evening.
 */
const COFFEE_SHAPE: Readonly<Record<number, number>> = {
  6: 0,
  7: 0.42,
  8: 1,
  9: 0.86,
  10: 0.55,
  12: 0.1,
  14: -1,
  16: -0.6,
  18: -0.95,
  20: -0.85,
  22: -0.65,
};

/**
 * The Ignite line's shape, 0..1 above where he wakes. Authored from the
 * product's own facts in docs/ignite-cognition-brief.md -- onset inside 15
 * minutes, peak within the first hour, 80 mg of natural caffeine (about one
 * cup of coffee, taken mornings only) -- so the line cannot promise a flat day
 * the product does not claim: it eases off from mid-afternoon and is back at
 * his waking level by ten. The brief states no duration. The operator gave
 * eight hours on 2026-09-11, and this shape already sat comfortably inside it
 * -- still near its peak at three in the afternoon, reaching zero only at ten
 * at night -- so the fade was not re-drawn for it. If that number moves again,
 * check this shape against it rather than assuming it still fits.
 */
const IGNITE_SHAPE: Readonly<Record<number, number>> = {
  6: 0,
  7: 0.22,
  8: 0.88,
  9: 1,
  10: 1,
  12: 0.94,
  14: 0.84,
  16: 0.62,
  18: 0.38,
  20: 0.16,
  22: 0,
};

/**
 * Shape factors, all read off HIS answers.
 *
 * `start` is where he wakes. `spike` is how hard caffeine lifts him. `crash`
 * is how far he falls in the afternoon. The Ignite line is authored from the
 * product's own facts rather than his answers, except that it peaks a fixed
 * step above his coffee peak so the two are drawn to the same scale.
 *
 * The values are a SHAPE, not a measurement -- the rendered chart carries no
 * numbered axis because focus has no unit.
 */
export function deriveDayCurve(answers: Answers): DayPoint[] {
  const waking = str(answers, "waking");
  const start =
    waking === "rested" ? 0.5 : waking === "slow-start" ? 0.38 : waking === "waking-at-2" ? 0.26 : 0.32;

  // A man who does not drink coffee gets no caffeine spike drawn on his day:
  // the small morning lift left is the ordinary wake-up rise, not a stimulant.
  // (Plan 3 must label his line "your day now" rather than "on coffee".)
  const coffee = str(answers, "coffee-effect");
  const spike =
    coffee === "wires-then-drops"
      ? 0.52
      : coffee === "need-more"
        ? 0.34
        : coffee === "no-coffee"
          ? 0.1
          : 0.3;

  const wall = str(answers, "afternoon-wall");
  const crash =
    wall === "almost-daily" ? 0.68 : wall === "few-times-week" ? 0.45 : wall === "after-bad-sleep" ? 0.3 : 0.2;

  const floor = Math.max(0.12, start - crash * 0.3);

  // Both rises are SCALED into the headroom above where he wakes, never
  // clamped. A clamp pinned the Ignite line at 1.0 from eight to two for a
  // rested waker with a hard coffee spike -- a flat line, the one shape this
  // file says it must never draw -- and collapsed the gap between the two
  // peaks to nothing. Scaling keeps the Ignite peak a fixed fraction above the
  // coffee peak on every answer set, so the chart argues the same gap for
  // everyone and the shape of each line survives intact.
  // The Ignite rise is at least the ordinary coffee spike plus a step, so a
  // non-coffee drinker's Ignite line is not shrunk by his flat baseline.
  const igniteAmplitude = Math.max(spike, 0.3) + 0.14;
  const headroom = Math.min(1, (1 - start) / igniteAmplitude);
  const coffeeRise = spike * headroom;
  const igniteRise = igniteAmplitude * headroom;

  return DAY_HOURS.map((hour) => {
    const c = COFFEE_SHAPE[hour] ?? 0;
    const coffeeValue = c >= 0 ? start + c * coffeeRise : start + c * (start - floor);
    return {
      hour,
      coffee: clamp(coffeeValue),
      ignite: clamp(start + igniteRise * (IGNITE_SHAPE[hour] ?? 0)),
    };
  });
}
